'use client'

import { useTranslations } from 'next-intl'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { popModal, pushModal } from '@/components/modals'
import { AlertCircle } from 'lucide-react'
import { useWorkspaces } from '@/hooks/subscriptions/use-workspaces'

interface WorkspaceInsufficientCoinsModalProps {
  workspaceName?: string
  workspaceId?: string
}

/**
 * Modal shown to non-admin/owner members when workspace coins are insufficient
 */
export function WorkspaceInsufficientCoinsModal({
  workspaceName,
  workspaceId,
}: WorkspaceInsufficientCoinsModalProps) {
  const t = useTranslations('modals.workspace_insufficient_coins_modal')
  const { workspaces } = useWorkspaces()

  // Get workspace name from workspaces if not provided
  const displayWorkspaceName =
    workspaceName ||
    (workspaceId
      ? workspaces.find((w) => w._id.toString() === workspaceId)?.name
      : null) ||
    'this workspace'

  return (
    <DialogContent className="sm:max-w-[500px]">
      <DialogHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
            <AlertCircle className="h-5 w-5 text-amber-500" />
          </div>
          <div>
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogDescription className="mt-1">
              {displayWorkspaceName !== 'this workspace'
                ? t('description_with_workspace', {
                    workspace: displayWorkspaceName,
                  })
                : t('description')}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>
      <div className="mt-4 space-y-4">
        <div className="rounded-lg bg-white/5 p-4">
          <p className="text-sm text-white/70">{t('message')}</p>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline_2"
            onClick={() => popModal()}
            className="w-full sm:w-auto"
          >
            {t('close')}
          </Button>
          <Button
            onClick={() => {
              popModal()
              pushModal('PricingModal', {
                type: 'out_of_credit',
              })
            }}
            className="w-full sm:w-auto"
          >
            {t('self_recharge_button')}
          </Button>
        </div>
      </div>
    </DialogContent>
  )
}
